import { Colors, Lightning } from "@lightningjs/sdk";
import { MovieDetailsCover } from "./MovieDetailsCover";
import { MediaDurationWithProgress } from "../../../pages/vod/components/MediaDurationWithProgress/MediaDurationWithProgress";
import { MediaActions } from "../../../pages/vod/components/MediaActions/MediaActions";
import theme from "../../../lib/theme";
import { MediaModel } from "../../../lib/models";

interface MovieCompactTemplateSpec extends Lightning.Component.TemplateSpec {
    data: MediaModel;

    Top: {
        BaseInfo: {
            Cover: typeof MovieDetailsCover;
            Info: {
                Name: object;
                Labels: {
                    Year: typeof Object;
                    Duration: typeof MediaDurationWithProgress;
                };
            };
        };
        Description: object;
        Buttons: typeof MediaActions;
    };
}

export class MovieCompact
    extends Lightning.Component<MovieCompactTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<MovieCompactTemplateSpec>
{
    static override _template(): Lightning.Component.Template<MovieCompactTemplateSpec> {
        return {
            collision: true,
            w: theme.movie.content.w,
            h: (h) => h,
            x: (w) => w / 2,
            mountX: 0.5,
            Top: {
                collision: true,
                mountY: 0.5,
                y: (h) => h / 2,
                w: theme.movie.content.w,
                h: (h) => h,
                flex: { direction: "column", justifyContent: "center" },
                BaseInfo: {
                    w: (w) => w,
                    flex: { direction: "row", justifyContent: "flex-start" },
                    Cover: {
                        type: MovieDetailsCover
                    },
                    Info: {
                        flex: { direction: "column", justifyContent: "center" },
                        Name: {
                            flexItem: { marginBottom: 8 },
                            w: theme.movie.content.w - theme.mediaItem.w - 34,
                            text: {
                                text: "",
                                fontSize: 48,
                                lineHeight: 50,
                                textOverflow: "ellipsis",
                                fontFace: "Heavy",
                                wordWrap: true,
                                maxLines: 3
                            }
                        },
                        Labels: {
                            flex: { direction: "row" },
                            flexItem: { marginBottom: 10 },
                            Year: {
                                text: {
                                    text: "",
                                    fontSize: 30,
                                    wordWrap: true,
                                    lineHeight: 30,
                                    fontFace: "Secondary-Regular"
                                }
                            },
                            Duration: {
                                type: MediaDurationWithProgress
                            }
                        }
                    }
                },
                Buttons: {
                    h: 60,
                    flexItem: { marginTop: 24 },
                    type: MediaActions,
                    hasPlay: true
                },
                Description: {
                    flexItem: { marginTop: 24 },
                    w: theme.movie.content.w,
                    text: {
                        text: "",
                        fontSize: 30,
                        lineHeight: 36,
                        textOverflow: "ellipsis",
                        wordWrap: true,
                        maxLines: 12,
                        textColor: Colors(theme.colors.text.primary).get()
                    }
                }
            }
        };
    }

    readonly Top = this.getByRef("Top")!;
    readonly Buttons = this.Top.getByRef("Buttons")!;

    readonly BaseInfo = this.Top.getByRef("BaseInfo")!;
    readonly Cover = this.BaseInfo.getByRef("Cover")!;
    readonly Info = this.BaseInfo.getByRef("Info")!;
    readonly Name = this.Info.getByRef("Name")!;
    readonly Labels = this.Info.getByRef("Labels")!;
    readonly Duration = this.Labels.getByRef("Duration")!;
    readonly Year = this.Labels.getByRef("Year")!;
    readonly Description = this.Top.getByRef("Description")!;

    set data(movie: MediaModel) {
        this.Cover?.patch({
            textureSrc: movie.coverUrl
        });

        this.Name.patch({
            text: {
                text: movie.title || ""
            }
        });

        this.Description.patch({
            text: {
                text: movie.description || ""
            }
        });

        this.Duration.patch({
            duration: movie.duration,
            progress: movie.progress?.current
        });

        const hasDuration = movie.duration;
        const year = movie.releaseYear?.toString() || "";
        this.Year.patch({
            text: {
                text: year + (year && hasDuration ? "  •" : "")
            },
            flexItem: { marginRight: !!year ? 10 : 0 }
        });

        this.Buttons.patch({
            data: movie
        });
    }

    override _getFocused() {
        return this.Buttons;
    }
}
